import { m } from 'motion/react';
import { type FC } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import Info from '@/components/Icons/Info';
import * as format from '@/format';
import { priceWithT } from '@/format';
import usePopover from '@/hooks/usePopover';
import { type Invoice } from '@/typings/api-models';

import s from './InvoiceProgressTooltip.module.scss';

type Props = {
  invoice: Pick<Invoice, 'amountPaid' | 'amountTotal' | 'amountPending' | 'amountDue' | 'currency'>;
  hideIcon?: boolean;
  hideProgress?: boolean;
  className?: string;
  title?: string;
};

const InvoiceProgressTooltip: FC<Props> = ({
  invoice,
  hideIcon,
  hideProgress,
  className = '',
  title = 'Remaining Balance: ',
}) => {
  const { t } = useTranslation();
  const { isOpen, setIsOpen, menuRef, triggerRef } = usePopover('bottom-left');
  return (
    <div>
      <m.div
        ref={triggerRef as unknown as React.RefObject<HTMLDivElement>}
        className={s.progress}
        onMouseEnter={() => setIsOpen(true)}
        onMouseLeave={() => setIsOpen(false)}
      >
        <div className={s.info}>
          {!hideIcon && <Info weight='fill' size={12} color='var(--color-gray-100)' />}
          <p className={`caption ${className}`}>
            {title}
            {priceWithT.price(t, { currency: invoice.currency, price: invoice.amountDue }, false)}
          </p>
        </div>
        {!hideProgress && (
          <div className={s.bars}>
            <progress value={invoice.amountPending + invoice.amountPaid} max={invoice.amountTotal} />
            <progress value={invoice.amountPaid} max={invoice.amountTotal} />
          </div>
        )}
      </m.div>
      {isOpen &&
        createPortal(
          <m.ul
            ref={menuRef as unknown as React.RefObject<HTMLUListElement>}
            className={s.tooltip}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 4 }}
          >
            <li>
              Invoice Total{' '}
              <b>{priceWithT.price(t, { currency: invoice.currency, price: invoice.amountTotal }, false)}</b>
            </li>
            <li>
              {invoice.amountPaid > 0 || (invoice.amountPending === 0 && invoice.amountPaid === 0) ? 'Paid' : ''}
              {invoice.amountPaid > 0 && invoice.amountPending > 0 ? ' and ' : ''}
              {invoice.amountPending > 0 ? 'Pending' : ''}
              <b>
                {priceWithT.price(
                  t,
                  { currency: invoice.currency, price: invoice.amountPaid + invoice.amountPending },
                  false,
                )}
              </b>
            </li>
            <li>
              Remaining Balance <b>{format.price.withCurrency(invoice.amountDue, invoice.currency)}</b>
            </li>
          </m.ul>,
          document.body,
        )}
    </div>
  );
};

export default InvoiceProgressTooltip;
